import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import qs from "qs";
import api from "@services/api";

async function fetchStores(page, pageSize) {
  const query = qs.stringify(
    { page, page_size: pageSize },
    { encodeValuesOnly: true }
  );
  const { data } = await api.get(`/store?${query}`);
  return data;
}

export function useStoresQuery(page, pageSize, activeMarketplaceId) {
  return useQuery({
    // activeMarketplaceId is part of the key so the list refetches the moment
    // the marketplace context resolves (e.g. right after login, before that
    // resolution the API has no X-Marketplace-Id header and — for a
    // marketplace-admin user — returns every store, unscoped). Without this,
    // that first unscoped fetch stays cached and other marketplaces' stores
    // (e.g. Flash Wash, MSF Hospital) keep reappearing in the list. (WN-0154)
    queryKey: ["store", page, pageSize, activeMarketplaceId ?? "all"],
    queryFn: () => fetchStores(page, pageSize),
    placeholderData: keepPreviousData
  });
}

/**
 * Flat list of the active marketplace's stores for a picker (e.g. the customer
 * Default Store field). The list endpoint is franchise-scoped, so a marketplace
 * user only sees their own stores. Returns [{store_id, name, is_marketplace_default}].
 */
export function useStoreOptionsQuery(activeMarketplaceId) {
  return useQuery({
    queryKey: ["store-options", activeMarketplaceId ?? "all"],
    queryFn: async () => {
      const { data } = await api.get("/store?page=1");
      const rows = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
      return rows.map((s) => ({
        store_id: Number(s.store_id),
        name: s.name,
        is_marketplace_default: Number(s.is_marketplace_default) ? 1 : 0,
      }));
    },
    staleTime: 60_000,
  });
}

export function useCreateStoreMutation() {
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post("/store", payload);
      return data;
    }
  });
}

export function useUpdateStoreMutation() {
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const { data } = await api.post(`/store/${id}`, payload);
      return data;
    }
  });
}

export function useDeleteStoreMutation() {
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await api.delete(`/store/${id}`);
      return data;
    }
  });
}

